import { useLazyQuery } from '@apollo/client';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { LIVE_STATUS, PAGE_TYPE } from '../../../../../common/constants';
import { GET_PAGE_V2_VIDEO_URL } from '../../../../../graphql/Queries';
import { useApp } from '../../../../../providers/AppContext';

// LiveVideoContext for managing live video data
export const LiveVideoContext = createContext();

export const DEFAULT_POLLING_INTERVAL_SECONDS = 5;

// LiveVideoProvider - Handles all live video data fetching
export function LiveVideoProvider({
  children,
  enabled = false,
  slug,
  liveInfo,
  liveStatus: initLiveStatus,
  video,
  videoAsset,
  isPremium,
  moduleId
}) {
  const [status, setStatus] = useState(initLiveStatus);
  const [prevStatus, setPrevStatus] = useState(initLiveStatus);

  const initLiveVideoUrl = videoAsset?.url ?? null;
  const [liveVideoUrl, setLiveVideoUrl] = useState(initLiveVideoUrl);
  const [hasFetchedLiveUrl, setHasFetchedLiveUrl] = useState(!!liveVideoUrl);

  const { data: sessionData } = useSession();
  const {
    state: { workspaceConfig }
  } = useApp();

  const seconds =
    workspaceConfig?.POLLING_INTERVAL?.value ||
    DEFAULT_POLLING_INTERVAL_SECONDS;

  const [getPageV2] = useLazyQuery(GET_PAGE_V2_VIDEO_URL, {
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        token: sessionData?.accessToken
      }
    },
    onError: () => {}
  });

  const fetchVideoUrlFromPageV2 = useCallback(
    async (pageSlug) => {
      const { data: pageData } = await getPageV2({
        variables: {
          where: { type: PAGE_TYPE?.VIDEO, slug: pageSlug },
          filter: { moduleIds: [moduleId] }
        }
      });

      const videoUrl =
        pageData?.pageV2?.modules?.[0]?.moduleData?.config?.video?.videoAsset
          ?.url || null;

      return videoUrl;
    },
    [getPageV2, moduleId]
  );

  // Constants for live status
  const pollingInterval = seconds * 1000; // 5 seconds

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.NEXT_APP_STATIC_DATA_URL}/videos/${process.env.NEXT_APP_WORKSPACE_ID}/live-statuses.json`
      );

      if (response?.data) {
        const updatedStatus = response?.data[slug];

        const validStatuses = Object.values(LIVE_STATUS);
        if (updatedStatus && !validStatuses.includes(updatedStatus)) {
          // eslint-disable-next-line no-console
          console.warn(`Invalid live status received: ${updatedStatus}`);
          setStatus(null);
          setPrevStatus(null);
          setLiveVideoUrl(initLiveVideoUrl);
          return;
        }

        setPrevStatus(status);
        setStatus(updatedStatus);

        if (
          updatedStatus === LIVE_STATUS.LIVE &&
          status !== LIVE_STATUS.LIVE &&
          !isPremium &&
          !hasFetchedLiveUrl
        ) {
          try {
            setLiveVideoUrl(initLiveVideoUrl);
            const videoUrl = await fetchVideoUrlFromPageV2(slug);

            setLiveVideoUrl(videoUrl);
            setHasFetchedLiveUrl(true);
          } catch (assetError) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch live URL:', assetError);
            setLiveVideoUrl(initLiveVideoUrl);
          }
        } else if (updatedStatus === LIVE_STATUS.VOD_READY && !isPremium) {
          try {
            setLiveVideoUrl(initLiveVideoUrl);
            const videoUrl = await fetchVideoUrlFromPageV2(slug);
            setLiveVideoUrl(videoUrl);
          } catch (assetError) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch asset URL:', assetError);
            setLiveVideoUrl(initLiveVideoUrl);
          }
        } else if (
          updatedStatus === LIVE_STATUS.PAUSED ||
          updatedStatus === LIVE_STATUS.COMPLETED
        ) {
          setLiveVideoUrl(initLiveVideoUrl);
          setHasFetchedLiveUrl(false);
        } else if (status === LIVE_STATUS.LIVE) {
          setHasFetchedLiveUrl(false);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch live status:', error);
      setStatus(null);
      setPrevStatus(null);
      setLiveVideoUrl(initLiveVideoUrl);
      return error;
    }
  }, [
    slug,
    isPremium,
    fetchVideoUrlFromPageV2,
    initLiveVideoUrl,
    hasFetchedLiveUrl,
    status
  ]);

  useEffect(() => {
    if (enabled && !isPremium) {
      if (status && status !== LIVE_STATUS.VOD_READY) {
        fetchData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status, isPremium]);

  useEffect(() => {
    if (enabled && !isPremium) {
      if (status && status !== LIVE_STATUS.VOD_READY) {
        const intervalId = setInterval(fetchData, pollingInterval);
        return () => {
          clearInterval(intervalId);
        };
      }
    }
  }, [enabled, fetchData, status, isPremium, pollingInterval]);

  const liveStatus = status;

  const isLive = enabled && liveStatus === LIVE_STATUS.LIVE;
  const isPaused = enabled && liveStatus === LIVE_STATUS.PAUSED;
  const isPending = enabled && liveStatus === LIVE_STATUS.PENDING;
  const isVodReady = enabled && liveStatus === LIVE_STATUS.VOD_READY;
  const isCompleted = enabled && liveStatus === LIVE_STATUS.COMPLETED;
  const isVodProcessing = enabled && liveStatus === LIVE_STATUS.VOD_PROCESSING;

  const contextValue = useMemo(
    () => ({
      video,
      isLive,
      enabled,
      isPaused,
      fetchData,
      isPending,
      liveStatus,
      isVodReady,
      prevStatus,
      isCompleted,
      isVodProcessing,
      liveData: { liveInfo, videoUrl: liveVideoUrl }
    }),
    [
      video,
      isLive,
      enabled,
      liveInfo,
      isPaused,
      fetchData,
      isPending,
      prevStatus,
      liveStatus,
      isVodReady,
      isCompleted,
      liveVideoUrl,
      isVodProcessing
    ]
  );

  return (
    <LiveVideoContext.Provider value={contextValue}>
      {children}
    </LiveVideoContext.Provider>
  );
}

// Hook to use LiveVideoContext
export function useLiveVideo() {
  const context = useContext(LiveVideoContext);
  if (!context) {
    throw new Error('useLiveVideo must be used within a LiveVideoProvider');
  }
  return context;
}
